import { useState, useEffect, useCallback } from "react";
import { Segment, Header, FormField, Dropdown, Input } from "semantic-ui-react";
import { SelectField } from "../../../../../../../../components/form/fields";
import { DescriptorProps } from "../../../../../../../../constnats/descriptor";
import { NO_OP_CALC } from "../../../../../../../../constnats/schemaConstants";
import { DataCalcType, DescriptorCategoryProps } from "../../../../../../../../models/descriptorModel";
import KeyValue from "../../../../../../../../structures/keyValePair";
import CloneUtils from "../../../../../../../../utils/cloneUtils";
import DescriptorType from "../../../../../../../../utils/descriptor/descriptorType";
import { CustomFieldSpec } from "../../../../../../../../utils/htmlFieldsFactory";
import CategoriesRepo from "../../../../../../../../utils/repository/categoriesRepo";
import ScaleUtil, { NAMES_TO_SCALES_MAP } from "../../../../../../../../utils/scaleUtil";
import { CalcField } from "../calculation";
import StandardizedLabelsRepo from "../../../../../../../../utils/repository/standardizedLabelsRepo/standardizedLabelsRepo";
import { RESPONSE_STATUS } from "../../../../../../../../constnats/general";
import { TIMEFRAME_PERIODS, TIMEFRAME_PERIOD } from "../../../../../../../../constnats/timeframe";
import _ from "lodash";

const ALLOWED_REPEATED_STANDARDIZED_LABELS = ["N/A"];

export const SCALE_MAP = ScaleUtil.scalePairs.filter(p => p.key !== 0);

export const IMPORTANCE_MAP = [
    KeyValue.createSame(0), KeyValue.createSame(3), KeyValue.createSame(5), KeyValue.createSame(10)
];

export const DESCRIPTOR_TYPES_MAP = DescriptorType.getAllTypes()
    .map(t => new KeyValue(t, t.charAt(0).toUpperCase() + t.slice(1)));

export const DATA_CALC_TYPES_MAP = DataCalcType.getAllTypes().map(KeyValue.createSame);

export const CATEGORY_SIGNS_MAP = [
    new KeyValue(false, 'Plus'), new KeyValue(true, 'Minus')
];

export function numFieldsDisabled({ descriptor }) {
    return !DescriptorType.isNumber(descriptor);
}

export function primaryCalcDisabled({ descriptor }) {
    return !DescriptorType.isNumber(descriptor) && !DescriptorType.isPercent(descriptor);
}

export function validatePrimaryCalc({ descriptor }) {
    return !primaryCalcDisabled({ descriptor }) || descriptor[DescriptorProps.CALCULATION] === NO_OP_CALC;
}

export function secondaryCalcDisabled({ descriptor }) {
    return primaryCalcDisabled({ descriptor }) || descriptor[DescriptorProps.CALCULATION] === NO_OP_CALC;
}

export function validateSecondaryCalc({ descriptor }) {
    return !secondaryCalcDisabled({ descriptor }) || descriptor[DescriptorProps.SECONDARY_CALCULATION] === NO_OP_CALC;
}

export function validateDataCalcType({ descriptor }) {
    const calcType = descriptor[DescriptorProps.DATA_CALC_TYPE];
    const hasVerticalCalc = descriptor[DescriptorProps.CALCULATION] !== NO_OP_CALC;
    let valid = true;
    if (DescriptorType.isHeadline(descriptor)) {
        valid = calcType === DataCalcType.NA;
    } else if (DescriptorType.isPercent(descriptor)) {
        valid = [DataCalcType.NA, DataCalcType.NON_CUMULATIVE].includes(calcType)
            || (hasVerticalCalc && calcType === DataCalcType.VERTICAL);
    } else if (DescriptorType.isNumber(descriptor)) {
        valid = [DataCalcType.NA, DataCalcType.NON_CUMULATIVE, DataCalcType.CUMULATIVE].includes(calcType)
            || (hasVerticalCalc && calcType === DataCalcType.VERTICAL);
    } else if (DescriptorType.isAny(descriptor)) {
        valid = [DataCalcType.NA, DataCalcType.NON_CUMULATIVE].includes(calcType);
    } else {
        valid = false;
    }

    return valid;
}

export function validateIScale({ descriptor }) {
    return DescriptorType.isNumber(descriptor) || descriptor[DescriptorProps.INPUT_SCALE] === 1;
}

export function validateVScale({ descriptor }) {
    return DescriptorType.isNumber(descriptor) || descriptor[DescriptorProps.VIEW_SCALE] === 1;
}

export function validationScalable({ descriptor }) {
    return DescriptorType.isNumber(descriptor) || descriptor[DescriptorProps.SCALABLE] === false;
}

export function getCalcFieldCfgSpec(prop, label, checkDisabled, validation) {
    return new CustomFieldSpec(prop, {
        dynamicPropsProvider: context => {
            return {
                descriptors: context.descriptors,
                flatDescriptors: context.flatDescriptors,
                forFieldId: context.descriptor.id
            };
        },
        checkDisabled,
        valueProp: "calculation",
        validate: validation,
        customStaticProps: {
            header: label
        }
    }, CalcField);
}

const SEARCH_DEBOUNCE_TIME = 2000;
export function StandardizedLabelRelationSectionField({ onChange, error, disabled, value, schemaId, descriptorId, usedStandardizedLabels }) {
    const [standardizedLabels, setStandardizedLabels] = useState([]);
    const [searching, setSearching] = useState(true);

    function fetchRecommendations() {
        setSearching(true)
        StandardizedLabelsRepo.listStandardizedLabels(
            (response) => {
                if (response.status === RESPONSE_STATUS.SUCCESS) {
                    setStandardizedLabels(
                        response.data
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter((a) => !usedStandardizedLabels.includes(a.id) || ALLOWED_REPEATED_STANDARDIZED_LABELS.includes(a.name)),
                    );
                    setSearching(false);
                }
            },
            (error) => {},
        );
    }

    useEffect(() => {
        fetchRecommendations();
    }, []);

    function searchStandardizedLabel(searchTerm) {
        //TODO
    }

     const handleSearchChange = useCallback(
            _.debounce(
                searchStandardizedLabel,
                SEARCH_DEBOUNCE_TIME
            ),
            []
        );

    const currentId = value?.standardizedLabelId ?? null;
    const currentOverwrite = value?.standardizedLabelOverwrite ?? "";

    const options = [
        { key: null, value: null, text: 'No Label' },
        ...standardizedLabels.map((a) => ({ key: a.id, value: a.id, text: a.name })),
    ];

    function handleDropdownChange(selectedId) {
        if (selectedId === null) {
            onChange(null);
        } else {
            onChange({ standardizedLabelId: selectedId, standardizedLabelOverwrite: currentOverwrite });
        }
    }

    function handleOverwriteChange(newText) {
        onChange({ standardizedLabelId: currentId, standardizedLabelOverwrite: newText });
    }

    return (
        <Segment>
            <Header as="h5" textAlign='center'>Standardized Label</Header>
            <FormField>
                <label>Label</label>
                <Dropdown
                    loading={searching}
                    onSearchChange={(_,data) => handleSearchChange(data.searchQuery)}
                    error={error}
                    disabled={disabled}
                    value={currentId}
                    options={options}
                    fluid
                    search
                    selection
                    onChange={(__, data) => handleDropdownChange(data.value)}
                />
            </FormField>
            <FormField>
                <label>Label Overwrite</label>
                <Input
                    error={error}
                    disabled={disabled || currentId === null}
                    value={currentOverwrite}
                    fluid
                    onChange={(_, data) => handleOverwriteChange(data.value)}
                />
            </FormField>
        </Segment>
    )
}

export function TimeframeSectionField({ onChange, error, disabled, value, flatDescriptors, descriptorId }) {
    const currentPeriod = value?.period ?? null;
    const currentReferenceId = value?.referenceId ?? null;
    const referenceIdDisabled = disabled || currentPeriod === null || currentPeriod === TIMEFRAME_PERIOD.REPORT_PERIOD
    const periodOptions = [
        { key: null, value: null, text: 'No Timeframe' },
        ...TIMEFRAME_PERIODS.map((p) => ({ key: p, value: p, text: p })),
    ];

    const referenceOptions = [
        { key: null, value: null, text: 'No Reference' },
        ...flatDescriptors
            .filter(
                (d) =>
                    d.id !== descriptorId &&
                    d[DescriptorProps.TIMEFRAME]?.period &&
                    d[DescriptorProps.TIMEFRAME]?.period !== TIMEFRAME_PERIOD.LTM,
            )
            .map((d) => ({ key: d.id, value: d.id, text: `${d.label} (${d.id})` })),
    ];

    function handlePeriodChange(newPeriod) {
        if (newPeriod === null) {
            onChange(null);
        } else {
            onChange({ period: newPeriod, referenceId: currentReferenceId });
        }
    }

    function handleReferenceChange(newReferenceId) {
        onChange({ period: currentPeriod, referenceId: newReferenceId });
    }

    return (
        <Segment>
            <Header as="h5" textAlign='center'>Timeframe</Header>
            <FormField>
                <label>Period</label>
                <Dropdown
                    error={error}
                    disabled={disabled}
                    value={currentPeriod}
                    options={periodOptions}
                    fluid
                    selection
                    onChange={(__, data) => handlePeriodChange(data.value)}
                />
            </FormField>
            {!referenceIdDisabled && (
                <FormField>
                    <label>Reference Id</label>
                    <Dropdown
                        error={error}
                        disabled={referenceIdDisabled}
                        value={currentReferenceId}
                        options={referenceOptions}
                        fluid
                        search
                        selection
                        onChange={(__, data) => handleReferenceChange(data.value)}
                    />
                </FormField>
            )}
        </Segment>
    );
}

function getCategoryInternal(category, categoryConfigured) {
    let categoryInternal = null
    if (category === null) {
        if (categoryConfigured) {
            categoryInternal = { [DescriptorCategoryProps.CATEGORY_ID]: 0, [DescriptorCategoryProps.INVERSESIGN]: false }
        } else {
            categoryInternal = { [DescriptorCategoryProps.CATEGORY_ID]: -1, [DescriptorCategoryProps.INVERSESIGN]: false }
        }
    } else {
        categoryInternal = category
    }

    return categoryInternal
}

export function CategoriesSectionField({ category, onChange, error, disabled, additionalPropChange, categoryConfigured }) {
    const [categories, setCategories] = useState([])
    const categoryInternal = getCategoryInternal(category, categoryConfigured)
    const categoryId = categoryInternal[DescriptorCategoryProps.CATEGORY_ID]

    useEffect(() => CategoriesRepo.getAll(setCategories), [])
    const options = [{ key: -1, value: -1, text: "Not Configured" }, { key: 0, value: 0, text: "No Category", }, ...categories.map(x => ({
        key: x.id, value: x.id, text: `${x.name}(${x.id})`
    }))]

    function propChangeHandler(categoryField) {
        return newVal => {
            const category = CloneUtils.deepClone(categoryInternal)
            category[categoryField] = newVal

            const categoryId = category[DescriptorCategoryProps.CATEGORY_ID]
            if (categoryId > 0) {
                Object.assign(category, categories.find(c => categoryId == c.id))
                onChange(category)
            } else {
                onChange(null)
            }

            if (categoryId >= 0) {
                additionalPropChange(true)
            } else {
                additionalPropChange(false)
            }
        }
    }



    return (
        <Segment>
            <Header as="h5" textAlign='center'>Category</Header>
            <FormField>
                <label>Category Name</label>
                <Dropdown
                    error={error}
                    disabled={disabled}
                    value={categoryId}
                    options={options}
                    fluid
                    search
                    selection
                    onChange={(__, data) => propChangeHandler(DescriptorCategoryProps.CATEGORY_ID)(data.value)}

                />
            </FormField>

            {categoryInternal[DescriptorCategoryProps.CATEGORY_ID] > 0 && (
                <SelectField
                    {...{ error, disabled }}
                    value={categoryInternal[DescriptorCategoryProps.INVERSESIGN]}
                    valuesMap={CATEGORY_SIGNS_MAP}
                    label="Category Relation"
                    onChange={propChangeHandler(DescriptorCategoryProps.INVERSESIGN)}
                />)}
        </Segment>
    )
}

export function InputScaleSelectionField(props) {
    const handleChange = (newVal) => {
        if(props.value === props[DescriptorProps.VIEW_SCALE]) {
            props.setViewScale(newVal)
        }
        if(newVal === NAMES_TO_SCALES_MAP.Actual) {
            props.setScalable(false)
        }
        props.onChange(newVal)
    }

    return (
        <SelectField
            disabled={props.disabled}
            error={props.error}
            label={props.label}
            number={props.number}
            onChange={handleChange}
            value={props.value}
            valuesMap={SCALE_MAP}
        />
    )
}
